import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

export type Theme = "light" | "dark" | "system";

interface ThemeContextValue {
  theme: Theme;
  /** What's actually applied right now ("dark" or "light"). When theme
   *  is "system" this resolves to the OS preference. */
  effective: "dark" | "light";
  setTheme: (next: Theme) => void;
  /** Cycle light -> dark -> system -> light. Used by the header toggle. */
  cycleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const STORAGE_KEY = "fxdash.theme";

function readStoredTheme(): Theme {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === "light" || raw === "dark" || raw === "system") return raw;
  } catch {
    // localStorage blocked — fall through to system default.
  }
  return "system";
}

function systemPrefersDark(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function applyEffective(eff: "dark" | "light") {
  // Briefly enable the transition class so colour swaps animate, then
  // remove it so unrelated hovers don't pay the cost on every render.
  const html = document.documentElement;
  html.classList.add("theme-transition");
  if (eff === "dark") html.classList.add("dark");
  else html.classList.remove("dark");
  // Flush the transition class on the next frame.
  window.setTimeout(() => html.classList.remove("theme-transition"), 250);
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => readStoredTheme());
  const [effective, setEffective] = useState<"dark" | "light">(() => {
    if (typeof document !== "undefined" && document.documentElement.classList.contains("dark")) {
      return "dark";
    }
    return "light";
  });

  // Re-resolve the effective theme whenever the user's choice changes,
  // and whenever the OS preference flips (only matters when theme=system).
  useEffect(() => {
    function resolve(): "dark" | "light" {
      if (theme === "system") return systemPrefersDark() ? "dark" : "light";
      return theme;
    }
    const next = resolve();
    setEffective(next);
    applyEffective(next);

    if (theme === "system") {
      const mq = window.matchMedia("(prefers-color-scheme: dark)");
      const onChange = () => {
        const e = mq.matches ? "dark" : "light";
        setEffective(e);
        applyEffective(e);
      };
      mq.addEventListener("change", onChange);
      return () => mq.removeEventListener("change", onChange);
    }
  }, [theme]);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // ignore
    }
  }, []);

  const cycleTheme = useCallback(() => {
    setTheme(theme === "light" ? "dark" : theme === "dark" ? "system" : "light");
  }, [theme, setTheme]);

  return (
    <ThemeContext.Provider value={{ theme, effective, setTheme, cycleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside ThemeProvider");
  return ctx;
}
